import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  approveAiDraft,
  rejectAiDraft,
  applyAiBundle,
  applyAiDraftItem,
  cancelAiBundle,
  undoAiDraftItem,
  type ApplyBundleResult,
  type ApplyBundleResultItem,
} from '../api/drafts';
import { aiQueryKeys } from './query-keys';

const invalidateBundleAndList = (qc: ReturnType<typeof useQueryClient>, bundleId?: string) => {
  qc.invalidateQueries({ queryKey: [...aiQueryKeys.all, 'bundles'] });
  if (bundleId) qc.invalidateQueries({ queryKey: aiQueryKeys.bundle(bundleId) });
  qc.invalidateQueries({ queryKey: aiQueryKeys.pendingApprovals() });
};

export const useApproveAiDraft = (bundleId?: string) => {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (draftId: string) => approveAiDraft(draftId),
    onSuccess: () => invalidateBundleAndList(qc, bundleId),
  });
};

export const useRejectAiDraft = (bundleId?: string) => {
  const qc = useQueryClient();
  return useMutation<void, Error, { draftId: string; reason?: string }>({
    mutationFn: ({ draftId, reason }) => rejectAiDraft(draftId, reason),
    onSuccess: () => invalidateBundleAndList(qc, bundleId),
  });
};

export const useApplyAiBundle = () => {
  const qc = useQueryClient();
  return useMutation<ApplyBundleResult, Error, string>({
    mutationFn: (bundleId: string) => applyAiBundle(bundleId),
    onSuccess: (_data, bundleId) => invalidateBundleAndList(qc, bundleId),
  });
};

export const useApplyAiDraftItem = (bundleId?: string) => {
  const qc = useQueryClient();
  return useMutation<ApplyBundleResultItem, Error, string>({
    mutationFn: (draftId: string) => applyAiDraftItem(draftId),
    onSuccess: () => invalidateBundleAndList(qc, bundleId),
  });
};

export const useCancelAiBundle = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, { bundleId: string; reason?: string }>({
    mutationFn: ({ bundleId, reason }) => cancelAiBundle(bundleId, reason),
    onSuccess: (_data, vars) => invalidateBundleAndList(qc, vars.bundleId),
  });
};

export const useUndoAiDraftItem = (bundleId?: string) => {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (draftId: string) => undoAiDraftItem(draftId),
    onSuccess: () => invalidateBundleAndList(qc, bundleId),
  });
};
